import React, {Fragment, useEffect, useState} from 'react';
import {useQueryClient} from "react-query";
import __ from "lodash";
import moment from "moment";
import { FormControl as MuiFormControl, FormGroup as MuiFormGroup, Grid as MuiGrid, Box as MuiBox, Tooltip, IconButton, Icon, TextField, Table, TableHead, TableRow, TableCell, TableBody} from "@material-ui/core";
import { ComboSelect, LabelledPaper, MaskedNumber } from '../../../controls';
import { useAirportCosts, useCostCategories, useCostingSectors, useCostingServices } from '../../../services/costings';
import { useSettings } from '../../../services/settings';
import { calculateRevenue } from '../includes/costingDialogFunctions';

const paperProps = {
  style: {
    backgroundColor: "#f8f8f8",
    boxShadow: 'none',
  }
}

const LabelledBox = (props) => {
  return (
    <LabelledPaper
      checkChildren={true}
      style={{display: "block", border: "solid 1px #b9b9b9" }}
      labelStyle={{background: "#f0f0f0"}}
      collapsible={false}
      {...__.omit(props, ['children'])}
    >
      {props.children}
    </LabelledPaper>
  )
}

const FormControl = (props) => {
  return (
    <MuiFormControl
      checkChildren={true}
      style={{display: "block" }}
      {...__.omit(props, ['children'])}
    >
      {props.children}
    </MuiFormControl>
  )
}

const Box = (props) => {
  return (
    <MuiBox
      checkChildren={true}
      {...__.omit(props, ['children'])}
    >
      {props.children}
    </MuiBox>
  )
}

const Grid = (props) => {
  return (
    <MuiGrid
      checkChildren={true}
      {...__.omit(props, ['children'])}
    >
      {props.children}
    </MuiGrid>
  )
}

const FormGroup = (props) => {
  return (
    <MuiFormGroup
      checkChildren={true}
      style={{padding: "8px 8px 8px 8px"}}
      {...__.omit(props, ['children'])}
    >
      {props.children}
    </MuiFormGroup>
  )
}

const SectionHeading = (props) => {
  const { children, ...other } = props;
  return (
    <div style={{marginTop: 10, marginBottom: 2, textAlign: "left", fontSize: "14px", fontWeight: "bold", ...other}}>
      {children}
    </div>
  );
}

const RevenueGrandTotal = (props) => {
  const sectors = props?.costingSectors||[];
  let total = 0;
  sectors.forEach((sector => {
    total += sector.revenueAmount;
  }))

  if (total) {
    return (<MaskedNumber displayType="text" value={total} prefix="$" decimalScale={2} fixedDecimalScale={true} />);
  }
  return (<span>-</span>)
}

const CostsGrandTotal = (props) => {
  const sectors = props?.costingSectors||[];
  let total = 0;
  sectors.forEach((sector => {
    total += sector.totalCost;
  }))

  if (total) {
    return (<MaskedNumber displayType="text" value={total} prefix="$" decimalScale={2} fixedDecimalScale={true} />);
  }
  return (<span>-</span>)
}

const ContributionGrandTotal = (props) => {
  const sectors = props?.costingSectors||[];
  let total = 0;
  sectors.forEach((sector => {
    const commission = (sector.commissionAmount || 0) + (sector.revenueAmount * ((sector.commissionRevenuePercent || 0) / 100));
    total += (sector.revenueAmount - (sector.totalCost + commission));
  }))

  return (<MaskedNumber displayType="text" value={total} decimalScale={2} fixedDecimalScale={true} prefix={(total) > 0 ?"$":"$("} suffix={(total) > 0 ?"":")"} style={{color: (total) > 0 ? undefined : "#DD0000"}}/>);
}

const CostingRevenueEditor = (_props) => {
  const props = {
    canEdit: false,
    ..._props,
  }

  const {record, systemData: SystemData, classes, eventBus, rebuildRunning, onChange, readOnly, noHeader} = props;

  const costingSectors = [...record?.legs];
  const contingencyBlockHoursRate = record?.contingencyBlockHoursRate || 0;

  const handleFieldChanged = (field, value, sector) => {
    sector[field] = value;
    switch (field) {
      case 'revenueCalculationMethod':
        sector['revenueCalculationAmount'] = 0;
        if (value === 'ACMI PLUS' || value === 'ACMI ONLY') {
          sector['revenueCalculationAmount'] = record?.acmiRate || 0;
        }
      // eslint-disable-next-line no-fallthrough
      case 'revenueCalculationAmount':
        sector['revenueAmount'] = calculateRevenue(sector, contingencyBlockHoursRate);
        break;
      default:
        break;
    }
    if (field === 'revenueCalculationMethod') {
      sector['revenueCalculationAmount'] = 0;
      if (value === 'ACMI PLUS' || value === 'ACMI ONLY') {
        sector['revenueCalculationAmount'] = record?.acmiRate || 0;
      }
    }
    if (onChange) {
      onChange(costingSectors);
    }
  }

  if (record) {
    const maskedNumberExtraParams = readOnly?{displayType: 'text'}:{};

    return (
      <div key="CostingRevenueEditorContainer" style={{width: "100%", textAlign:"left" }}>
          <Grid containerspacing={3}>
            <Grid item xs={12} row>
              <LabelledBox label="" >
                <FormControl key="FC-addresses" >
                  <FormGroup key="FG-addresses">
                    {!noHeader
                      ? <SectionHeading>Revenue</SectionHeading>
                      : <></>
                    }
                    <Table className={classes.revenue}>
                      <TableHead>
                        <TableRow>
                          <TableCell>Sectors</TableCell>
                          <TableCell width="80px" align="right">Payload</TableCell>
                          <TableCell width="80px" align="right">Total Cost</TableCell>
                          {/* <TableCell width="80px" align="right">Rates</TableCell>
                          <TableCell width="80px" align="right">Lump Sum Revenue</TableCell> */}
                          <TableCell width="100px" align="left">Revenue Model</TableCell>
                          <TableCell width="80px" align="right">Amount</TableCell>
                          <TableCell width="80px" align="right">Total Revenue</TableCell>
                          <TableCell width="80px" align="right">Commission</TableCell>
                          <TableCell width="80px" align="right">Contribution</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {costingSectors.map((sector) => {
                          const commission = (sector.commissionAmount || 0) + (sector.revenueAmount * ((sector.commissionRevenuePercent || 0) / 100));
                          return (
                          <TableRow>
                            <TableCell style={{fontWeight: 700}}><Box sx={{ flexDirection: 'row', width: "100%", justifyContent: "space-between"}}><span>{sector.originAirportCode}</span><span>-</span><span className={classes.airport}>{sector.destinationAirportCode}</span></Box></TableCell>
                            <TableCell width="80px" align="right"><MaskedNumber displayType="text" value={sector.payloadOnboard} decimalScale={0} fixedDecimalScale={true} suffix={record.weightUom==="TON"?" tons":" kg"}/></TableCell>
                            <TableCell width="80px" align="right" style={{backgroundColor: '#f0f0f0', fontWeight: 400}}><MaskedNumber displayType="text" value={sector.totalCost} decimalScale={2} fixedDecimalScale={true} prefix="$" /></TableCell>
                            <TableCell width="220px" align="left">
                              <ComboSelect
                                id="revenueCalculationMethod"
                                group='COSTING REVENUE METHODS'
                                subGroup=''
                                returnType="value"
                                inputLabelProps={{shrink: true}}
                                value={sector.revenueCalculationMethod}
                                onChange={(value) => handleFieldChanged('revenueCalculationMethod', value, sector)}
                                displayOnly={readOnly}
                              />
                            </TableCell>
                            <TableCell width="80px" align="right">
                              <div>
                                {sector.revenueCalculationMethod === 'COST PLUS'?
                                  <MaskedNumber {...maskedNumberExtraParams} value={sector.revenueCalculationAmount} decimalScale={2} fixedDecimalScale={true} valueCap={999999.99} prefix="$" onChange={(value) => handleFieldChanged('revenueCalculationAmount', value, sector)}/>
                                : null}
                                {sector.revenueCalculationMethod === 'PERCENT UPLIFT'?
                                  <MaskedNumber {...maskedNumberExtraParams} value={sector.revenueCalculationAmount} decimalScale={0} fixedDecimalScale={true} valueCap={100} suffix="%" onChange={(value) => handleFieldChanged('revenueCalculationAmount', value, sector)}/>
                                : null}
                                {sector.revenueCalculationMethod === 'PER KG'?
                                  <MaskedNumber {...maskedNumberExtraParams} value={sector.revenueCalculationAmount} decimalScale={2} fixedDecimalScale={true} valueCap={9999.99} prefix="$" onChange={(value) => handleFieldChanged('revenueCalculationAmount', value, sector)}/>
                                : null}
                                {sector.revenueCalculationMethod === 'MARKET'?
                                  <MaskedNumber {...maskedNumberExtraParams} value={sector.revenueCalculationAmount} decimalScale={2} fixedDecimalScale={true} valueCap={9999.99} prefix="$" onChange={(value) => handleFieldChanged('revenueCalculationAmount', value, sector)}/>
                                : null}
                                {sector.revenueCalculationMethod === 'VALUE'?
                                  <MaskedNumber {...maskedNumberExtraParams} value={sector.revenueCalculationAmount} decimalScale={2} fixedDecimalScale={true} valueCap={9999999.99} prefix="$" onChange={(value) => handleFieldChanged('revenueCalculationAmount', value, sector)}/>
                                : null}
                                {sector.revenueCalculationMethod === 'ACMI ONLY'?
                                  <MaskedNumber {...maskedNumberExtraParams} value={sector.revenueCalculationAmount} decimalScale={2} fixedDecimalScale={true} valueCap={9999999.99} prefix="$" onChange={(value) => handleFieldChanged('revenueCalculationAmount', value, sector)}/>
                                : null}
                                {sector.revenueCalculationMethod === 'ACMI PLUS'?
                                  <MaskedNumber {...maskedNumberExtraParams} value={sector.revenueCalculationAmount} decimalScale={2} fixedDecimalScale={true} valueCap={9999999.99} prefix="$" onChange={(value) => handleFieldChanged('revenueCalculationAmount', value, sector)}/>
                                : null}
                              </div>
                            </TableCell>
                            {/* <TableCell width="80px" align="right"><MaskedNumber displayType="text" value={sector.revenueRate} decimalScale={2} fixedDecimalScale={true} prefix="$" /></TableCell> */}
                            {/* <TableCell width="80px" align="right"><MaskedNumber displayType="text" value={sector.additionalRevenue} decimalScale={2} fixedDecimalScale={true} prefix="$"/></TableCell> */}
                            <TableCell width="80px" align="right" style={{backgroundColor: '#d9d9d9', fontWeight: 700}}>
                              <MaskedNumber displayType="text" value={sector.revenueAmount} decimalScale={2} fixedDecimalScale={true} prefix="$"/>
                            </TableCell>
                            <TableCell width="80px" align="right">
                              <MaskedNumber displayType="text" value={commission} decimalScale={2} fixedDecimalScale={true} prefix="$"/>
                            </TableCell>
                            <TableCell width="80px" align="right" style={{backgroundColor: '#f0f0f0', fontWeight: 700}}>
                              <MaskedNumber displayType="text" value={sector.revenueAmount - (sector.totalCost + commission)} decimalScale={2} fixedDecimalScale={true} prefix={(sector.revenueAmount - (sector.totalCost + commission)) > 0 ?"$":"$("} suffix={(sector.revenueAmount - sector.totalCost) > 0 ?"":")"} style={{color: (sector.revenueAmount - (sector.totalCost + commission)) > 0 ? undefined : "#DD0000"}}/>
                            </TableCell>
                          </TableRow>
                        )})}
                        <TableRow>
                          <TableCell style={{backgroundColor: "#f0f0f0", fontWeight: 700}}>Total</TableCell>
                          <TableCell colspan={1} style={{backgroundColor: "#f0f0f0", fontWeight: 700}}></TableCell>
                          <TableCell style={{backgroundColor: "#d9d9d9", fontWeight: 400}} width="100px" align="right"><CostsGrandTotal costingSectors={costingSectors}/></TableCell>
                          <TableCell colspan={2} style={{backgroundColor: "#f0f0f0", fontWeight: 700}}></TableCell>
                          <TableCell style={{backgroundColor: "#c9c9c9", fontWeight: 700}} width="100px" align="right"><RevenueGrandTotal costingSectors={costingSectors}/></TableCell>
                          <TableCell colspan={1} style={{backgroundColor: "#f0f0f0", fontWeight: 700}}></TableCell>
                          <TableCell style={{backgroundColor: '#d9d9d9', fontWeight: 700}} width="100px" align="right"><ContributionGrandTotal costingSectors={costingSectors}/></TableCell>
                        </TableRow>
                      </TableBody>
                    </Table>
                  </FormGroup>
                </FormControl>
              </LabelledBox>
            </Grid>
          </Grid>
      </div>
    );
  }
  return (<></>);
}

export default CostingRevenueEditor;
